import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/header";
import Footer from "../components/footer";
import Sidebar from "../components/sidebar";
import HousingCompanySelector from "../components/housingCompanySelector";
import HousingCompanyInfoCard from "../components/housingCompanyInfoCard";
import { useUser } from "../context/useUser";
import { apiFetch } from "../api/client";
import { getRoleLabelFi } from "../utils/roleLabels";

export default function CompanyAndStaircase() {
  const { user, selectedCompany, setSelectedCompany } = useUser();
  const navigate = useNavigate();

  const [selectedCompanyValue, setSelectedCompanyValue] = useState("");
  const [activeCompany, setActiveCompany] = useState(null);
  const [staircaseTypes, setStaircaseTypes] = useState([]);
  const [selectedStaircaseId, setSelectedStaircaseId] = useState("");
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loadingStaircases, setLoadingStaircases] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!selectedCompany?.housing_company_id) return;

    setSelectedCompanyValue(`${selectedCompany.housing_company_id}:${selectedCompany.role}`);
    setActiveCompany(selectedCompany);
    setSelectedStaircaseId(selectedCompany.staircase_type_id ? String(selectedCompany.staircase_type_id) : "");
  }, [selectedCompany]);

  useEffect(() => {
    if (!user?.token || !activeCompany?.housing_company_id) {
      setCompanyInfo(null);
      return;
    }

    async function loadCompanyInfo() {
      try {
        const data = await apiFetch(`/user/housing-company/${activeCompany.housing_company_id}`, {
          token: user.token,
        });
        setCompanyInfo(data);
      } catch (err) {
        console.error("Failed to load housing company info:", err);
        setCompanyInfo(null);
      }
    }

    loadCompanyInfo();
  }, [activeCompany?.housing_company_id, user?.token]);

  useEffect(() => {
    if (!user?.token || !activeCompany?.housing_company_id) {
      setStaircaseTypes([]);
      return;
    }

    async function loadStaircaseTypes() {
      try {
        setLoadingStaircases(true);
        setError("");

        const data = await apiFetch(
          `/form/staircase-types?housing_company_id=${activeCompany.housing_company_id}`,
          { token: user.token }
        );

        setStaircaseTypes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load staircase types:", err);
        setStaircaseTypes([]);
        setError("Rapputyyppien lataus ei onnistunut.");
      } finally {
        setLoadingStaircases(false);
      }
    }

    loadStaircaseTypes();
  }, [activeCompany?.housing_company_id, user?.token]);

  const handleCompanyChange = (value, company) => {
    setSelectedCompanyValue(value);
    setActiveCompany(company);
    setSelectedStaircaseId(company?.housing_company_id === selectedCompany?.housing_company_id
      ? String(selectedCompany?.staircase_type_id || "")
      : "");
    setError("");
  };

  const activateSummaryForSelection = async ({ company, staircaseId, navigateToNextStep = false }) => {
    if (!company?.housing_company_id || !staircaseId) {
      setError("Valitse ensin taloyhtiö ja rapputyyppi.");
      return;
    }

    const sameActiveSummary =
      Number(selectedCompany?.housing_company_id) === Number(company.housing_company_id)
      && Number(selectedCompany?.staircase_type_id) === Number(staircaseId)
      && selectedCompany?.summaryId;

    if (sameActiveSummary) {
      if (navigateToNextStep) {
        navigate("/waterpipes");
      }
      return;
    }

    try {
      setSaving(true);
      setError("");

      const summary = await apiFetch(
        `/form/summary/current?housing_company_id=${company.housing_company_id}&staircase_type_id=${staircaseId}`,
        { token: user.token }
      );

      const selectedStaircase = staircaseTypes.find(
        (staircase) => String(staircase.id) === String(staircaseId)
      );

      setSelectedCompany({
        id: company.housing_company_id,
        ...company,
        staircase_type_id: Number(staircaseId),
        staircase_type_name: selectedStaircase?.name || "",
        summaryId: summary.id,
        summaryData: summary,
      });

      window.dispatchEvent(new Event("cart-updated"));

      if (navigateToNextStep) {
        navigate("/waterpipes");
      }
    } catch (err) {
      console.error("Failed to create or load summary:", err);
      setError(err?.body?.error || "Valintojen aloitus ei onnistunut.");
    } finally {
      setSaving(false);
    }
  };

  const handleStaircaseChange = async (nextStaircaseId) => {
    setSelectedStaircaseId(String(nextStaircaseId));

    if (!activeCompany?.housing_company_id) {
      return;
    }

    await activateSummaryForSelection({
      company: activeCompany,
      staircaseId: String(nextStaircaseId),
      navigateToNextStep: false,
    });
  };

  const handleContinue = async () => {
    await activateSummaryForSelection({
      company: activeCompany,
      staircaseId: selectedStaircaseId,
      navigateToNextStep: true,
    });
  };

  const hasCurrentSelection = Boolean(selectedCompany?.summaryId && selectedCompany?.staircase_type_id);

  return (
    <>
      <Header />
      <div className="options-form-container">
        <Sidebar />

        <main className="options-form-content">
          <h1 className="default-title">Taloyhtiö ja rapputyyppi</h1>

          <div className="card-box selection-setup-card">
            <div className="selection-setup-grid">
              <HousingCompanySelector
                value={selectedCompanyValue}
                onChange={handleCompanyChange}
                label="Valitse taloyhtiö"
              />

              {activeCompany && (
                <div className="selection-setup-summary default-text">
                  <p><strong>Valittu taloyhtiö:</strong> {activeCompany.company_name}</p>
                  <p><strong>Roolisi:</strong> {getRoleLabelFi(activeCompany.role)}</p>
                </div>
              )}
            </div>
          </div>

          {activeCompany && <HousingCompanyInfoCard companyInfo={companyInfo} />}

          <div className="card-box selection-setup-card">
            <p className="category-title">Valitse rapputyyppi</p>

            {!activeCompany ? (
              <p className="default-text">Valitse ensin taloyhtiö, niin näet sen käytettävissä olevat rapputyypit.</p>
            ) : loadingStaircases ? (
              <p className="default-text">Ladataan rapputyyppejä...</p>
            ) : staircaseTypes.length === 0 ? (
              <p className="default-text">Tälle taloyhtiölle ei löytynyt rapputyyppejä.</p>
            ) : (
              <div className="staircase-list">
                {staircaseTypes.map((staircase) => {
                  const isActive = String(staircase.id) === String(selectedStaircaseId);

                  return (
                    <label
                      key={staircase.id}
                      className={`staircase-card ${isActive ? "active" : ""}`}
                    >
                      <input
                        type="radio"
                        name="staircase-type"
                        checked={isActive}
                        onChange={() => handleStaircaseChange(staircase.id)}
                      />

                      <div>
                        <h3 className="staircase-card-title">{staircase.name}</h3>
                        <p className="default-text">{staircase.description || "Kuvausta ei ole lisätty tälle rapputyypille."}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {hasCurrentSelection && selectedCompany?.company_name && selectedCompany?.staircase_type_name && (
            <div className="card-box selection-setup-card default-text">
              <p><strong>Nykyinen valinta:</strong> {selectedCompany.company_name} / {selectedCompany.staircase_type_name}</p>
              <p>Voit vaihtaa taloyhtiön tai rapputyypin tältä sivulta milloin tahansa ennen kuin jatkat valintoihin.</p>
            </div>
          )}

          {error && <p className="default-text status-text error">{error}</p>}
          <div className="next-button-container">
            <button
              type="button"
              className="next-button"
              onClick={handleContinue}
              disabled={saving || !activeCompany || !selectedStaircaseId}
            >
              {saving ? "Tallennetaan..." : "Siirry valintoihin"}
            </button>
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
}
